import { useMemo, useState } from 'react'
import { Edit, Trash2 } from 'lucide-react'
import { ChurchForm } from '../components/ChurchForm'
import { DeleteChurch } from '../components/DeleteChurch'
import { ChurchCard } from '../components/ChurchCard'
import type { churchFilterSchema } from '@/schemas/filterSchemas'
import { ListPageHeader } from '@/components/custom/ListPageHeader'
import { PaginationWrapper } from '@/components/custom/PaginationWrapper'
import { TBody, TD, TH, THead, TR, Table } from '@/components/custom/table'
import { Badge } from '@/components/custom/badge'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useChurchesStore } from '@/features/churches/store/churchesStore'
import { useUsersStore } from '@/features/users/store/usersStore'
import { usePermissions } from '@/utils/permissions'
import { useCleanQueryStates, usePagination } from '@/hooks'

export default function Churches() {
  const churches = useChurchesStore((s) => s.churches)
  const users = useUsersStore((s) => s.users)
  const permissions = usePermissions()

  const pastors = users.filter((u) => u.roles.includes('PASTOR'))

  const [query, setQuery] =
    useCleanQueryStates<typeof churchFilterSchema>('/authed/churches')
  const [open, setOpen] = useState({ add: false, edit: false, delete: false })
  const [churchId, setChurchId] = useState<string | null>(null)

  const filtered = useMemo(
    () =>
      churches.filter((c) => {
        const matchSearch =
          query.search === '' ||
          c.name.toLowerCase().includes(query.search.toLowerCase()) ||
          pastors
            .find((p) => p._id === c.pastorId)
            ?.name.toLowerCase()
            .includes(query.search.toLowerCase()) ||
          c.address.city.toLowerCase().includes(query.search.toLowerCase())

        const matchPastor =
          query.pastorId.length === 0 ||
          query.pastorId.includes(c.pastorId as string)

        const matchCity =
          query.cities.length === 0 || query.cities.includes(c.address.city)

        const matchProvince =
          query.provinces.length === 0 ||
          query.provinces.includes(c.address.province)

        const matchActive =
          query.onlyActives === false || c.active === query.onlyActives

        return (
          matchSearch &&
          matchPastor &&
          matchCity &&
          matchProvince &&
          matchActive
        )
      }),
    [churches, pastors, query],
  )

  const { page, pageCount, rows, setPage, goToFirstPage } = usePagination({
    data: filtered,
    pageSize: 10,
  })

  return (
    <div className="space-y-3">
      <ListPageHeader
        type="churches"
        searchValue={query.search}
        onSearchChange={(value) => {
          setQuery({ search: value })
          goToFirstPage()
        }}
        initialFilters={query}
        onFilter={(filters) => {
          setQuery(filters)
          goToFirstPage()
        }}
        isFormOpen={open.add}
        onFormOpen={() => setOpen((o) => ({ ...o, add: true }))}
        onFormClose={() => setOpen((o) => ({ ...o, add: false }))}
        showAddButton={permissions.canCreateChurch()}
      />

      {/* Desktop Table */}
      <div className="hidden lg:block overflow-hidden rounded-md border bg-white">
        <Table>
          <THead>
            <TR>
              <TH>Name</TH>
              <TH>City</TH>
              <TH>Province</TH>
              <TH>Pastor</TH>
              <TH>Status</TH>
              <TH className="text-right">Actions</TH>
            </TR>
          </THead>
          <TBody>
            {rows.map((church) => {
              const churchPastor = pastors.find(
                (p) => p._id === church.pastorId,
              )
              return (
                <TR key={church._id}>
                  <TD>{church.name}</TD>
                  <TD>{church.address.city}</TD>
                  <TD>{church.address.province}</TD>
                  <TD>{churchPastor?.name}</TD>
                  <TD>
                    <Badge>{church.active ? 'Active' : 'Inactive'}</Badge>
                  </TD>
                  <TD className="text-right">
                    <div className="flex justify-end gap-2">
                      {permissions.canEditChurch(church._id) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setChurchId(church._id)
                            setOpen((o) => ({ ...o, edit: true }))
                          }}
                        >
                          <Edit className="h-4 w-4" /> Edit
                        </Button>
                      )}

                      {permissions.canDeleteChurch() && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => {
                            setChurchId(church._id)
                            setOpen((o) => ({ ...o, delete: true }))
                          }}
                        >
                          <Trash2 className="h-4 w-4" /> Delete
                        </Button>
                      )}
                    </div>
                  </TD>
                </TR>
              )
            })}
            {rows.length === 0 && (
              <TR>
                <TD className="p-6 text-center text-neutral-500" colSpan={6}>
                  No results
                </TD>
              </TR>
            )}
          </TBody>
        </Table>
      </div>

      {/* Mobile Cards */}
      <ScrollArea className="lg:hidden h-[calc(100vh-170px)]">
        <div className="space-y-3">
          {filtered.length === 0 ? (
            <div className="bg-white rounded-lg border p-6 text-center text-neutral-500">
              No churches found
            </div>
          ) : (
            filtered.map((church) => {
              const churchPastor = pastors.find(
                (p) => p._id === church.pastorId,
              )
              return (
                <ChurchCard
                  key={church._id}
                  church={church}
                  pastorName={churchPastor?.name}
                  permissions={permissions}
                  onEdit={(id) => {
                    setChurchId(id)
                    setOpen((o) => ({ ...o, edit: true }))
                  }}
                  onDelete={(id) => {
                    setChurchId(id)
                    setOpen((o) => ({ ...o, delete: true }))
                  }}
                />
              )
            })
          )}
        </div>
      </ScrollArea>

      {/* Desktop Pagination */}
      <div className="hidden lg:block">
        <PaginationWrapper
          pageCount={pageCount}
          currentPage={page}
          onPageChange={setPage}
        />
      </div>

      {/* Single shared components */}
      <ChurchForm
        id={churchId}
        type="edit"
        open={open.edit}
        onClose={() => {
          setOpen((o) => ({ ...o, edit: false }))
          setChurchId(null)
        }}
      />

      <DeleteChurch
        id={churchId}
        open={open.delete}
        onClose={() => {
          setOpen((o) => ({ ...o, delete: false }))
          setChurchId(null)
        }}
      />
    </div>
  )
}
